import { useQuery } from '@apollo/client/react'
import { useMemo } from 'react'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { LogoStatus } from '../../__generated__/graphql/catalog/graphql'
import { LogoDigitizationSetting } from '../../__generated__/graphql/catalog/graphql'
import { useAuthentication } from '../../app/_providers/AuthenticationProvider.client'
import { useGraphqlClients } from '../../app/_providers/GraphqlClientsProvider.client'
import { CartContainer } from '../../brunswick/containers/cart'
import { LogoFile } from '../../containers/EmbroideryContainer/types'
import { QueryStatus } from '../../lib/hooks/types'

const MY_EMBROIDERY_LOGOS_QUERY = gql(`
  query myEmbroideryLogos {
    my {
      id
      embroideryLogos {
        id
        status
        name
        filename
        notes
        preview
        digitizationSetting
        manualHandling
      }
    }
  }
`)

/** Get all of the DTC embroidery logo files that are available for a given user */
export default function useEmbroideryLogos() {
  const { personalizedClient } = useGraphqlClients()
  const transientLogo = useTransientLogo()
  const { isLoggedIn } = useAuthentication()
  const { data, error, loading } = useQuery(MY_EMBROIDERY_LOGOS_QUERY, {
    client: personalizedClient,
    fetchPolicy: 'network-only',
    skip: !isLoggedIn,
  })

  /*
   * The logo files that are saved to the user's account.
   */
  const userLogos = useMemo<LogoFile[]>(() => {
    const savedLogos = data?.my.embroideryLogos ?? []
    return savedLogos.map(logo => ({
      digitizationSetting: logo?.digitizationSetting!,
      id: logo?.id ?? '',
      name: logo?.name ?? '',
      notes: logo?.notes ?? '',
      preview: logo?.preview ?? '',
      // per FIGS-5980 - only include filename if logo digitization has been approved
      filename: logo?.status === 'APPROVED' ? logo.filename : null,
      shopifyCustomerId: '',
      status: logo?.status as LogoStatus,
      manualHandling: logo?.manualHandling,
    }))
  }, [data])

  const status = useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    return 'resolved'
  }, [loading, error])

  return { userLogos, transientLogo, status }
}

/**
 * A "transient" logo is a logo + digitization request that exists in the user's cart, but has not
 * yet been purchased/saved. Only one of these should exist at any given time at most.
 *
 * The cart half of `useEmbroideryLogos`, without fetching the account's logos. Prices and logo
 * choices use `useNewLogoInOrder` instead, which also counts other garments on the page.
 */
export function useTransientLogo(): LogoFile | undefined {
  const { cart } = CartContainer.useContainer()

  return useMemo<LogoFile | undefined>(() => {
    const logoSetupItem = Object.values(cart.items).find(item => {
      return item.properties._is_logo_setup === 'true' && item.properties._logo_id
    })

    if (!logoSetupItem) return undefined

    const relatedEmbroideryItem = Object.values(cart.items).find(item => {
      return (
        item.productType.toUpperCase() === 'EMBROIDERY' &&
        item.properties._logo_id === logoSetupItem.properties._logo_id &&
        item.properties._logo_setup_guid === logoSetupItem.properties._guid
      )
    })

    const sourceItem = relatedEmbroideryItem ?? logoSetupItem

    return {
      digitizationSetting: sourceItem.properties.logo_color_option as LogoDigitizationSetting,
      id: sourceItem.properties._logo_id!,
      name: sourceItem.properties.logo_name!,
      filename: sourceItem.properties._logo_file_name ?? null,
      notes: sourceItem.properties.logo_note ?? null,
      preview: sourceItem.properties._logo_preview!,
      shopifyCustomerId: sourceItem.properties.shopify_customer_id!,
      status: sourceItem.properties.status as LogoStatus,
      manualHandling: sourceItem.properties._logo_manual_handling === 'true',
    }
  }, [cart.items])
}
